import { useTranslation } from 'react-i18next'
import { GitBranch, GitMerge, Route } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { Workflow, WorkflowPattern, WorkflowStatus } from '@/schemas/workflow'

interface WorkflowCardProps {
  workflow: Workflow
  onClick: () => void
}

const PATTERN_ICONS: Record<WorkflowPattern, React.ReactNode> = {
  chaining: <GitBranch className="w-4 h-4" />,
  parallel: <GitMerge className="w-4 h-4" />,
  routing: <Route className="w-4 h-4" />,
}

const STATUS_STYLES: Record<WorkflowStatus, string> = {
  draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  inactive: 'bg-red-500/20 text-red-300 border-red-500/30',
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function WorkflowCard({ workflow, onClick }: WorkflowCardProps) {
  const { t } = useTranslation('workflows')

  return (
    <Card
      variant="glass"
      className="p-6 cursor-pointer hover:scale-[1.02] transition-all duration-200"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-300">
            {PATTERN_ICONS[workflow.pattern_type]}
          </div>
        </div>
        <Badge
          className={cn(
            'border text-xs',
            STATUS_STYLES[workflow.status]
          )}
        >
          {t(`status.${workflow.status}`)}
        </Badge>
      </div>

      <div className="space-y-1 mb-4">
        <h3 className="text-lg font-semibold text-gray-100 truncate">
          {workflow.name}
        </h3>
        {workflow.description && (
          <p className="text-sm text-gray-400 line-clamp-2">
            {workflow.description}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs border-gray-700/50 text-gray-400">
            {t(`patterns.${workflow.pattern_type}.name`)}
          </Badge>
          <span>{t('card.version', { version: workflow.version })}</span>
        </div>
        <span>
          {t('card.lastModified')} {formatDate(workflow.updated_at)}
        </span>
      </div>
    </Card>
  )
}
